<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useDebounceFn } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  knowledgebaseId: {
    type: [String, Number],
    required: true,
  },
  sitemap: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();

const url = ref(props.sitemap.url || '');
const regex = ref(props.sitemap.regex || '');
const autoupdate = ref(props.sitemap.autoupdate ?? true);
const customized = ref(props.sitemap.customized ?? false);
const urls = ref([]);
const selectedUrls = ref([]);
const showAdvancedSettings = ref(!!props.sitemap.regex);
const previousUrl = ref('');
const previousRegex = ref('');

const uiFlags = computed(() => store.getters['sitemaps/getUIFlags']);

const rules = { url: { required } };
const v$ = useVuelidate(rules, { url });

const hasUrls = computed(() => urls.value.length > 0);

const fetchUrls = useDebounceFn(async () => {
  if (
    url.value &&
    (url.value !== previousUrl.value || regex.value !== previousRegex.value)
  ) {
    previousUrl.value = url.value;
    previousRegex.value = regex.value;
    try {
      const fetchedUrls = await store.dispatch('sitemaps/getUrls', {
        knowledgebaseId: props.knowledgebaseId,
        siteUrl: url.value,
        regex: regex.value,
      });
      urls.value = fetchedUrls;
      selectedUrls.value = fetchedUrls.map(u => u);
    } catch (error) {
      useAlert(t('LIBRARIES.SITEMAPS_MODAL.EDIT_ERROR'));
    }
  }
}, 1000);

watch(url, fetchUrls);
watch(regex, fetchUrls);

// Initial fetch of URLs for the current sitemap
fetchUrls();

const toggleSelectAll = () => {
  if (selectedUrls.value.length === urls.value.length) {
    selectedUrls.value = [];
    customized.value = true;
  } else {
    selectedUrls.value = urls.value.map(u => u);
    customized.value = true;
  }
};

watch(selectedUrls, newVal => {
  if (newVal.length !== urls.value.length) {
    customized.value = true;
  } else {
    customized.value = false;
  }
});

async function handleSave() {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    await store.dispatch('sitemaps/update', {
      knowledgebaseId: props.knowledgebaseId,
      siteId: props.sitemap.id,
      sitemap: {
        url: url.value,
        urls: selectedUrls.value,
        autoupdate: autoupdate.value,
        customized: customized.value,
        regex: regex.value,
      },
    });
    useAlert(t('LIBRARIES.SITEMAPS_MODAL.SAVE_SUCCESS'));
    emit('saved');
  } catch (error) {
    useAlert(t('LIBRARIES.SITEMAPS_MODAL.EDIT_ERROR'));
  }
}
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header
      :header-title="t('LIBRARIES.SITEMAPS_MODAL.EDIT_TITLE')"
    />

    <form
      class="flex flex-col w-full px-8 pb-8"
      @submit.prevent="handleSave"
      @keydown.enter.prevent="handleSave"
    >
      <div class="w-full">
        <woot-input
          v-model="url"
          :label="t('LIBRARIES.SITES.ADD.FORM.URL.LABEL')"
          type="text"
          class="mb-0"
          :class="{ error: v$.url.$error }"
          :error="v$.url.$error ? t('LIBRARIES.SITES.ADD.FORM.URL.ERROR') : ''"
          :placeholder="t('LIBRARIES.SITES.ADD.FORM.URL.PLACEHOLDER')"
          @blur="v$.url.$touch"
        />
      </div>

      <div class="w-full mb-4">
        <label class="flex items-center">
          <span class="mr-2">{{
            t('LIBRARIES.SITES.ADD.FORM.AUTOUPDATE.LABEL')
          }}</span>
          <Switch v-model="autoupdate" />
        </label>
      </div>

      <div class="w-full">
        <NextButton
          :label="t('LIBRARIES.SITES.ADD.FORM.ADVANCED_SETTINGS.TOGGLE')"
          :icon="{
            'i-lucide-chevron-down': !showAdvancedSettings,
            'i-lucide-chevron-up': showAdvancedSettings,
          }"
          sm
          link
          @click.prevent="showAdvancedSettings = !showAdvancedSettings"
        />
      </div>

      <div v-if="showAdvancedSettings" class="w-full my-4">
        <woot-input
          v-model="regex"
          :label="t('LIBRARIES.SITES.ADD.FORM.REGEX.LABEL')"
          type="text"
          :placeholder="t('LIBRARIES.SITES.ADD.FORM.REGEX.PLACEHOLDER')"
        />
        <p class="text-sm text-gray-500">
          {{ t('LIBRARIES.SITES.ADD.FORM.REGEX.DESCRIPTION') }}
        </p>
      </div>

      <div v-if="uiFlags.isFetchingUrls" class="flex justify-center py-4">
        <Spinner :size="48" />
      </div>
      <div v-else-if="hasUrls" class="w-full my-4">
        <div class="max-h-48 overflow-y-scroll">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider flex flex-row gap-2"
                >
                  <input
                    type="checkbox"
                    :checked="selectedUrls.length === urls.length"
                    @change="toggleSelectAll"
                  />
                  <span>{{ t('LIBRARIES.SITEMAPS_MODAL.SELECT') }}</span>
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {{ t('LIBRARIES.SITEMAPS_MODAL.URL') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(item, index) in urls" :key="index">
                <td class="px-6 py-2">
                  <input v-model="selectedUrls" type="checkbox" :value="item" />
                </td>
                <td class="px-6 py-2 text-sm text-neutral-700 break-all">
                  {{ item.loc || item }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 mt-6">
        <NextButton
          :label="t('LIBRARIES.SITES.CANCEL_BUTTON_TEXT')"
          sm
          slate
          @click.prevent="emit('close')"
        />
        <NextButton
          :label="t('LIBRARIES.SITEMAPS_MODAL.SAVE')"
          sm
          variant="solid"
          type="submit"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
